import { NodeToolbar, Position } from '@xyflow/react';
import * as Y from 'yjs';
import { NodeFloatingToolbar } from './node-floating-toolbar';
import { FrameFloatingToolbar } from './frame-floating-toolbar';
import type { FreedrawState } from './node-formatting-toolbar';
import type { GifState } from './node-floating-toolbar';
import type { NodeType } from '@/features/canvas/utils/node-types';
import type { FormattingState } from '@/features/canvas/engine/hooks/use-formatting-state';
import type { NodeActions } from '@/features/canvas/engine/wiring/contexts/node-actions-context';

export interface SelectionToolbarProps {
  nodeId: string;
  nodeType: NodeType;
  /** Whether the toolbar should be visible at all (selection + solo + not dragging). */
  isVisible: boolean;
  locked: boolean;
  /** Whether formatting controls should be embedded inside the
   *  non-frame toolbar. Frames always include their formatting block
   *  (it's part of FrameFloatingToolbar). */
  showFormatting: boolean;
  /** Live frame data — icon + collapsed. Drives the frame toolbar's
   *  state-aware affordances (collapse toggle icon/label, icon picker
   *  trigger). Ignored for non-frame nodes. */
  frameData: { icon: string; collapsed: boolean };
  /** Yjs doc — required by the frame toolbar (FrameFormattingToolbar
   *  reads/writes per-frame fields directly through this). */
  doc?: Y.Doc;
  formattingState?: FormattingState;
  /** Freedraw stroke state — only consulted by the non-frame toolbar
   *  for freedraw nodes. Other node types ignore. */
  freedrawState?: FreedrawState;
  /** GIF play/pause state — only consulted for gif nodes. */
  gifState?: GifState;
  /** Action handlers from NodeActionsContext. The toolbar consumes a
   *  subset based on node kind. */
  actions: NodeActions;
}

/**
 * Floating toolbar that appears above a selected node. Routes to the
 * frame-specific toolbar (Fill/Border + icon picker + collapse toggle
 * + frame action set) when the node is a frame, and to the generic
 * node toolbar otherwise.
 *
 * Mounted via React Flow's NodeToolbar primitive — the portal that
 * positions content above the node and tracks its position as the
 * canvas pans/zooms. SelectionToolbar wraps the portal so callers
 * (today: SelectionTransformer) only mount one component instead of
 * orchestrating the portal + the per-kind branch inline.
 */
export function SelectionToolbar({
  nodeId,
  nodeType,
  isVisible,
  locked,
  showFormatting,
  frameData,
  doc,
  formattingState,
  freedrawState,
  gifState,
  actions,
}: SelectionToolbarProps) {
  return (
    <NodeToolbar
      nodeId={nodeId}
      isVisible={isVisible}
      position={Position.Top}
      offset={40}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {nodeType === 'frame' && doc && formattingState && (
        <FrameFloatingToolbar
          doc={doc}
          nodeId={nodeId}
          locked={locked}
          collapsed={frameData.collapsed}
          icon={frameData.icon}
          formattingState={formattingState}
          onDelete={actions.onDelete}
          onToggleLock={actions.onToggleLock}
          onUngroup={actions.onUngroup}
        />
      )}
      {nodeType !== 'frame' && (
        <NodeFloatingToolbar
          nodeId={nodeId}
          nodeType={nodeType}
          locked={locked}
          onDelete={actions.onDelete}
          onDuplicate={actions.onDuplicate}
          onToggleLock={actions.onToggleLock}
          formattingState={showFormatting && formattingState ? formattingState : undefined}
          freedrawState={freedrawState}
          gifState={gifState}
        />
      )}
    </NodeToolbar>
  );
}
